import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Chip,
  Paper,
  Divider,
  Stack,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Code as CodeIcon,
  Receipt as ReceiptIcon,
  LocalShipping as LocalShippingIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authenticatedFetch } from '../../utils/api';
import DiaCircle from '../DiaCircle';
import GaugeChip from '../GaugeChip';
import ProgressBar from '../ProgressBar';
import { formatInteger } from '../../utils/formatters';
import { useTranslation, interpolate } from '../../i18n';
import { isCompanyRep, isAdmin, getCurrentUser } from '../../utils/permissions';

interface MachineProgram {
  program_id: number;
  company_name: string;
  company_id: number;
  status: string;
  has_shift_details: boolean;
  total_weight: number;
  completed_weight: number;
  remaining_weight: number;
  progress_percentage: number;
  total_rolls?: number;
  completed_rolls?: number;
  remaining_rolls?: number;
  rolls_progress_percentage?: number;
}

interface MachineStatus {
  dia: number;
  gauge: string;
  machine_type: string;
  programs: MachineProgram[];
  in_progress: MachineProgram[];
  not_started: MachineProgram[];
  is_idle: boolean;
}

interface Machine {
  id: number;
  dia: number;
  gauge: string;
  machine_type: string;
}

interface CompanyDashboardData {
  programs_count: number;
  received_notes_count: number;
  deliveries_count: number;
  invoices_count: number;
  company_name: string;
}

const Home: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [machineStatuses, setMachineStatuses] = useState<MachineStatus[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [companyData, setCompanyData] = useState<CompanyDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const { translations: t } = useTranslation();
  const navigate = useNavigate();
  
  const user = getCurrentUser();
  const userIsCompanyRep = isCompanyRep(user) && !isAdmin(user);

  useEffect(() => {
    if (userIsCompanyRep) {
      fetchCompanyRepData();
    } else {
      fetchData();
    }
  }, [userIsCompanyRep]);

  const fetchCompanyRepData = async () => {
    try {
      // For company_rep users, fetch summary data for their company
      const [programsRes, receivedNotesRes, deliveriesRes, invoicesRes, companyRes] = await Promise.all([
        authenticatedFetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/programs`),
        authenticatedFetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/received_notes`),
        authenticatedFetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/deliveries`),
        authenticatedFetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/invoices`),
        user?.company_id ? authenticatedFetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/companies/${user.company_id}`) : Promise.resolve(null),
      ]);
      
      const programs = programsRes.ok ? await programsRes.json() : [];
      const receivedNotes = receivedNotesRes.ok ? await receivedNotesRes.json() : [];
      const deliveries = deliveriesRes.ok ? await deliveriesRes.json() : [];
      const invoices = invoicesRes.ok ? await invoicesRes.json() : [];
      const company = companyRes && companyRes.ok ? await companyRes.json() : { name: 'Your Company' };
      
      setCompanyData({
        programs_count: Array.isArray(programs) ? programs.length : 0,
        received_notes_count: Array.isArray(receivedNotes) ? receivedNotes.length : 0,
        deliveries_count: Array.isArray(deliveries) ? deliveries.length : 0,
        invoices_count: Array.isArray(invoices) ? invoices.length : 0,
        company_name: company.name || 'Your Company',
      });
    } catch (error) {
      console.error('Error fetching company rep data:', error);
      setCompanyData(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const [statusResponse, machinesResponse] = await Promise.all([
        authenticatedFetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/machines/status`
        ),
        authenticatedFetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/machines`
        ),
      ]);
      
      if (!statusResponse.ok) {
        throw new Error('Failed to fetch machine status');
      }
      
      if (!machinesResponse.ok) {
        throw new Error('Failed to fetch machines');
      }
      
      const statusData = await statusResponse.json();
      const machinesData = await machinesResponse.json();
      
      // Ensure data is an array before setting state
      if (Array.isArray(statusData)) {
        setMachineStatuses(statusData);
      } else {
        console.error('Invalid data format received:', statusData);
        setMachineStatuses([]);
      }
      
      if (Array.isArray(machinesData)) {
        setMachines(machinesData);
      } else {
        console.error('Invalid machines data format received:', machinesData);
        setMachines([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setMachineStatuses([]); // Ensure state remains an array even on error
      setMachines([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Helper function to safely convert to number
  const toNumber = (value: any): number => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(num) || num === null || num === undefined ? 0 : num;
  };

  const getFilteredMachines = (gauge: string, tab: number) => {
    // Safety check: ensure machineStatuses is an array
    if (!Array.isArray(machineStatuses)) {
      console.error('machineStatuses is not an array:', machineStatuses);
      return [];
    }
    
    return machineStatuses.filter((machine) => {
      if (machine.gauge !== gauge) return false;

      switch (tab) {
        case 0: // In Progress
          return machine.in_progress.length > 0;
        case 1: // Not Yet Started
          return machine.not_started.length > 0;
        case 2: // Idle
          return machine.is_idle;
        default:
          return false;
      }
    });
  };

  const renderMachineCard = (machine: MachineStatus, program: MachineProgram | null, isIdle: boolean) => {
    return (
      <Card
        key={`${machine.dia}-${machine.gauge}-${machine.machine_type}-${program?.program_id || 'idle'}`}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.2s, box-shadow 0.2s',
          width: { xs: '85%', sm: '100%' },
          ml: { xs: 0, sm: 0 },
          mr: { xs: 'auto', sm: 0 },
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4,
          },
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1.5, sm: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <DiaCircle
                dia={machine.dia}
                gauge={machine.gauge}
                machineType={machine.machine_type}
                machines={machines}
                size="medium"
              />
            </Box>
            <GaugeChip gauge={machine.gauge} />
          </Box>

          <Box sx={{ mb: { xs: 1, sm: 1 } }}>
            <Chip
              label={machine.machine_type}
              size="small"
              sx={{
                bgcolor: machine.machine_type === 'rib' ? '#e0e0e0' : '#757575',
                color: machine.machine_type === 'rib' ? '#000' : '#fff',
              }}
            />
          </Box>

          {!isIdle && program && (
            <>
              <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />
              
              <Typography variant="body1" sx={{ mb: { xs: 0.75, sm: 1 }, fontWeight: 'bold', color: 'primary.main' }}>
                {program.company_name}
              </Typography>

              {/* Weight Progress */}
              <Box sx={{ mt: { xs: 1.5, sm: 2 } }}>
                <ProgressBar
                  type="weight"
                  done={toNumber(program.completed_weight)}
                  total={toNumber(program.total_weight)}
                  size="small"
                />
              </Box>

              {/* Rolls Progress (if constraint exists) */}
              {program.total_rolls && program.total_rolls > 0 && (
                <Box sx={{ mt: { xs: 1.5, sm: 1.5 } }}>
                  <ProgressBar
                    type="rolls"
                    done={toNumber(program.completed_rolls)}
                    total={toNumber(program.total_rolls)}
                    remainingWeight={toNumber(program.remaining_weight)}
                    size="small"
                  />
                </Box>
              )}
            </>
          )}

          {isIdle && (
            <Box sx={{ mt: { xs: 1.5, sm: 2 }, textAlign: 'center', py: { xs: 1.5, sm: 2 } }}>
              <Typography variant="body1" color="text.secondary">
                {t.home.noActivePrograms}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderGaugeSection = (gauge: string, tab: number) => {
    const filteredMachines = getFilteredMachines(gauge, tab);

    if (filteredMachines.length === 0) {
      return (
        <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
          <Typography variant="body1" color="text.secondary">
            {interpolate(t.home.noMachinesInCategory, { gauge })}
          </Typography>
        </Paper>
      );
    }

    return (
      <Box sx={{ overflow: 'hidden' }}>
        <Grid container spacing={{ xs: 1.5, sm: 3 }}>
          {filteredMachines.map((machine) => {
            if (tab === 2) {
              // Idle tab - show one card per machine
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={`${machine.dia}-${machine.gauge}-${machine.machine_type}`}>
                  {renderMachineCard(machine, null, true)}
                </Grid>
              );
            } else {
              // In Progress or Not Yet Started - show multiple cards if multiple programs
              const programsToShow = tab === 0 ? machine.in_progress : machine.not_started;
              return programsToShow.map((program) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={`${machine.dia}-${machine.gauge}-${machine.machine_type}-${program.program_id}`}>
                  {renderMachineCard(machine, program, false)}
                </Grid>
              ));
            }
          })}
        </Grid>
      </Box>
    );
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography>{t.home.loadingMachineStatus}</Typography>
      </Container>
    );
  }

  // Company Rep Dashboard
  if (userIsCompanyRep && companyData) {
    return (
      <Container maxWidth="lg" sx={{ overflow: 'hidden' }}>
        <Card sx={{ mt: { xs: 2, sm: 4 } }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ mb: { xs: 2, sm: 3 }, fontWeight: 'bold', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              {companyData.company_name}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: { xs: 3, sm: 4 } }}>
              Welcome to your company dashboard. View your programs, received notes, deliveries, and invoices below.
            </Typography>

            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card 
                  sx={{ 
                    cursor: 'pointer', 
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 },
                    bgcolor: 'primary.light',
                    color: 'primary.contrastText',
                  }}
                  onClick={() => navigate('/programs')}
                >
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <CodeIcon sx={{ fontSize: 48, mb: 1 }} />
                    <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                      {companyData.programs_count}
                    </Typography>
                    <Typography variant="body1">
                      {t.nav.programs}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card 
                  sx={{ 
                    cursor: 'pointer', 
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 },
                    bgcolor: 'success.light',
                    color: 'success.contrastText',
                  }}
                  onClick={() => navigate('/received-notes')}
                >
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <ReceiptIcon sx={{ fontSize: 48, mb: 1 }} />
                    <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                      {companyData.received_notes_count}
                    </Typography>
                    <Typography variant="body1">
                      {t.nav.receivedNotes}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card 
                  sx={{ 
                    cursor: 'pointer', 
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 },
                    bgcolor: 'info.light',
                    color: 'info.contrastText',
                  }}
                  onClick={() => navigate('/deliveries')}
                >
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <LocalShippingIcon sx={{ fontSize: 48, mb: 1 }} />
                    <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                      {companyData.deliveries_count}
                    </Typography>
                    <Typography variant="body1">
                      {t.nav.deliveries}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card 
                  sx={{ 
                    cursor: 'pointer', 
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 },
                    bgcolor: 'warning.light',
                    color: 'warning.contrastText',
                  }}
                  onClick={() => navigate('/invoices')}
                >
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <DescriptionIcon sx={{ fontSize: 48, mb: 1 }} />
                    <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                      {companyData.invoices_count}
                    </Typography>
                    <Typography variant="body1">
                      {t.nav.invoices}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ overflow: 'hidden' }}>
      <Card sx={{ mt: { xs: 2, sm: 4 } }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ mb: { xs: 2, sm: 3 }, fontWeight: 'bold', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            {t.home.title}
          </Typography>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: { xs: 2, sm: 3 } }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              aria-label="machine status tabs"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab label={t.home.inProgress} sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }} />
              <Tab label={t.home.notYetStarted} sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }} />
              <Tab label={t.home.idle} sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }} />
            </Tabs>
          </Box>

          {/* 14 GG Section */}
          <Box sx={{ mb: { xs: 3, sm: 4 } }}>
            <Typography variant="h5" gutterBottom sx={{ mb: { xs: 1.5, sm: 2 }, fontWeight: 'bold', color: 'primary.main' }}>
              {t.home.machines14GG}
            </Typography>
            {renderGaugeSection('14', tabValue)}
          </Box>

          {/* 16 GG Section */}
          <Box>
            <Typography variant="h5" gutterBottom sx={{ mb: { xs: 1.5, sm: 2 }, fontWeight: 'bold', color: 'primary.main' }}>
              {t.home.machines16GG}
            </Typography>
            {renderGaugeSection('16', tabValue)}
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Home;

